import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { InputerrorsComponent } from '../../inputerrors.component';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, InputerrorsComponent],
  template: `
    <div class="min-h-screen flex flex-col items-center justify-center bg-benzGrey-50 px-4 py-8">
      <div class="w-full max-w-md">
        
        <!-- Back Button -->
        <button
          type="button"
          (click)="goBack()"
          class="mb-6 flex items-center gap-2 text-benzGrey-600 hover:text-benzGrey-900 transition-colors focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded-lg p-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          <span>Zurück</span>
        </button>

        <!-- Card -->
        <div class="bg-white rounded-2xl shadow-lg p-8">
          
          @if (!emailSent()) {
            <!-- Request Reset Form -->
            <div>
              <!-- Header -->
              <div class="text-center mb-8">
                <div class="mx-auto w-16 h-16 bg-benzRed-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-benzRed-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
                  </svg>
                </div>
                <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Passwort vergessen?</h1>
                <p class="text-benzGrey-600">
                  Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
                </p>
              </div>

              <!-- Error Messages -->
              @if (errorMessage()) {
                <div class="mb-6 p-4 bg-benzRed-50 border border-benzRed-200 rounded-lg" role="alert">
                  <div class="flex items-start gap-3">
                    <svg class="w-5 h-5 text-benzRed-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                    </svg>
                    <p class="text-sm text-benzRed-700">{{ errorMessage() }}</p>
                  </div>
                </div>
              }

              <!-- Form -->
              <form [formGroup]="forgotPasswordForm" (ngSubmit)="requestReset()" class="flex flex-col gap-4">
                
                <!-- Email -->
                <app-inputerrors [control]="forgotPasswordForm.controls.email">
                  <label class="flex flex-col gap-2">
                    <span class="text-sm font-medium text-benzGrey-900">
                      E-Mail-Adresse <span class="text-benzRed-700">*</span>
                    </span>
                    <input
                      type="email"
                      formControlName="email"
                      placeholder="max@example.com"
                      [class.border-benzRed-700]="forgotPasswordForm.controls.email.touched && !forgotPasswordForm.controls.email.valid"
                      class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                      required>
                  </label>
                </app-inputerrors>

                <!-- Submit Button -->
                <button
                  type="submit"
                  [disabled]="isLoading() || !forgotPasswordForm.valid"
                  class="w-full mt-2 px-6 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 disabled:bg-benzGrey-300 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
                  @if (isLoading()) {
                    <span class="flex items-center justify-center gap-2">
                      <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Wird gesendet...</span>
                    </span>
                  } @else {
                    <span>Link zum Zurücksetzen senden</span>
                  }
                </button>
              </form>
            </div>
          } @else {
            <!-- Success Message -->
            <div class="text-center">
              <!-- Success Icon -->
              <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>

              <h2 class="text-2xl font-bold text-benzGrey-900 mb-3">E-Mail gesendet!</h2>
              <p class="text-benzGrey-600 mb-6">
                Wir haben Ihnen eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts an
                <strong class="text-benzGrey-900">{{ submittedEmail() }}</strong> gesendet.
              </p>

              <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                <p class="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Der Link ist 24 Stunden gültig. Falls Sie keine E-Mail erhalten, überprüfen Sie bitte Ihren Spam-Ordner.
                </p>
              </div>

              <button
                type="button"
                (click)="goToLogin()"
                class="w-full px-6 py-3 bg-benzRed-700 text-white font-semibold rounded-lg hover:bg-benzRed-800 transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
                Zurück zur Anmeldung
              </button>

              <button
                type="button"
                (click)="resetForm()"
                class="w-full mt-3 px-6 py-3 border-2 border-benzGrey-900 text-benzGrey-900 font-semibold rounded-lg hover:bg-benzGrey-50 transition-colors focus:outline-none focus:ring-2 focus:ring-benzGrey-900">
                Erneut senden
              </button>
            </div>
          }

          <!-- Divider -->
          <div class="relative my-8">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-benzGrey-200"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-4 bg-white text-benzGrey-600">oder</span>
            </div>
          </div>

          <!-- Back to Login -->
          <div class="text-center">
            <p class="text-sm text-benzGrey-600 mb-2">
              Passwort wieder eingefallen?
            </p>
            <a
              [routerLink]="['/auth/login']"
              class="text-benzRed-700 font-semibold hover:text-benzRed-800 hover:underline focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded">
              Zurück zur Anmeldung
            </a>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class AuthForgotPasswordComponent {
  private router = inject(Router);
  private authService = inject(AuthService);

  public forgotPasswordForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  public isLoading = signal(false);
  public errorMessage = signal<string | null>(null);
  public emailSent = signal(false);
  public submittedEmail = signal<string>('');

  requestReset(): void {
    if (!this.forgotPasswordForm.valid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const email = this.forgotPasswordForm.controls.email.value!;

    this.authService.requestPasswordReset(email).subscribe({
      next: () => {
        this.submittedEmail.set(email);
        this.emailSent.set(true);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.errorMessage.set(error.message || 'Fehler beim Senden der E-Mail');
        this.isLoading.set(false);
      }
    });
  }

  resetForm(): void {
    this.emailSent.set(false);
    this.forgotPasswordForm.reset();
    this.errorMessage.set(null);
  }

  goToLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  goBack(): void {
    this.router.navigate(['/auth/login']);
  }
}

